import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { cn } from 'lib/utils';
import { Loader, Sparkle } from 'lucide-react';
import { useCallback, useState, useEffect } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { IMAGE_GEN_AVAILABLE_STYLE } from '../constants/data';
import { ImageGenService } from '../services/image-gen.api';
import { saveFile, savePromptInfo, setFileName } from 'store/slices/imageGen';
import { useDispatch } from 'react-redux';
import z from 'zod';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';

const schema = z.object({
  input: z.string(),
  prompt_type: z.string(),
  token: z.string().optional(),
});

type TSchema = z.infer<typeof schema>;

export default function ImageGenerateForm() {
  const [selectedStyleType, setSelectedStyleType] = useState('Digital');
  const [isFormValid, setIsFormValid] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { mutate, isPending } = useMutation({
    mutationKey: ['generate-image-from-text'],
    mutationFn: ImageGenService.generateImageFromText,
    onSuccess: (data) => {
      navigate(`/text-to-image/result`);
      dispatch(
        savePromptInfo({
          tempImagePath: data.data.image_url,
          prompt_type: form.getValues('prompt_type'),
          input: form.getValues('input'),
        })
      );
      dispatch(saveFile({ fileSaved: false }));
      dispatch(setFileName({ fileName: 'Untitled 1' }));
    },
    onError: () => {
      toast.error('Something went wrong!', {
        position: 'bottom-center',
      });
    },
  });

  const form = useForm<TSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      input: '',
      prompt_type: '',
    },
    mode: 'onChange',
  });

  const { formState: { errors }, watch, register } = form;
  const input = watch('input');
  const promptType = watch('prompt_type');

  useEffect(() => {
    const inputValid = Boolean(input);
    const styleSelected = Boolean(promptType);
    setIsFormValid(inputValid && styleSelected);
  }, [input, promptType]);

  const onVerify = useCallback((token: string) => {
    form.setValue('token', token);
  }, [form]);

  // Force ReCAPTCHA to reload
  useEffect(() => {
    // Initial load
    setRefreshReCaptcha(true);

    // Reset after component mounts
    const timer = setTimeout(() => {
      setRefreshReCaptcha(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const onSubmit = (values: TSchema) => {
    try {
      mutate(values);
      
      // After submission, refresh ReCAPTCHA for next use
      setRefreshReCaptcha(true);
      setTimeout(() => setRefreshReCaptcha(false), 100);
      
    } catch (error) {
      console.error('Error generating image:', error);
    }
  };

  const handleGeneratePrompt = async () => {
    try {
      const response = await ImageGenService.generateImageFromRandomPrompt();
      if (response) {
        form.setValue('input', response);
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
    }
  };

  return (
    <div className="w-full text-text-primary max-w-3xl mx-auto bg-background rounded-xl">
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* Prompt Section */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium">Prompt</label>
            <button
              type="button"
              className="text-primary text-sm underline"
              onClick={handleGeneratePrompt}
            >
              Generate prompt
            </button>
          </div>
          <Input
            placeholder="Enter prompt"
            {...register('input')}
            className={cn(
              'w-full px-4 py-3 rounded-lg bg-background shadow-sm border border-gray-200',
              errors.input && 'border-red-500'
            )}
          />
          {errors.input && (
            <p className="text-red-500 text-xs">{errors.input.message}</p>
          )}
        </div>

        {/* Style Selection */}
        <div className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <label className="text-sm font-medium">Select style</label>
            
            {/* Style Type Toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                className={cn(
                  'px-6 py-2 rounded-lg text-sm font-medium transition-colors',
                  selectedStyleType === 'Digital' 
                    ? 'bg-primary text-white' 
                    : 'bg-background-card text-text-primary'
                )}
                onClick={() => setSelectedStyleType('Digital')}
              >
                Digital
              </button>
              <button
                type="button"
                className={cn(
                  'px-6 py-2 rounded-lg text-sm font-medium transition-colors',
                  selectedStyleType === 'Traditional' 
                    ? 'bg-primary text-white' 
                    : 'bg-background-card text-text-primary'
                )}
                onClick={() => setSelectedStyleType('Traditional')}
              >
                Traditional
              </button>
            </div>
          </div>

          {/* Style Grid */}
          <Controller
            control={form.control}
            name="prompt_type"
            render={({ field }) => (
              <div className="flex lg:grid lg:grid-cols-4 overflow-x-auto lg:overflow-x-visible gap-3 pb-4 lg:pb-0 -mx-4 lg:mx-0 px-4 lg:px-0">
                {(selectedStyleType === 'Digital' 
                  ? IMAGE_GEN_AVAILABLE_STYLE.digital_style.subStyles 
                  : IMAGE_GEN_AVAILABLE_STYLE.traditional_style.subStyles
                ).map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    className={cn(
                      'relative w-32 lg:w-full aspect-square rounded-lg border overflow-hidden transition-all duration-200 flex-shrink-0', // Added fixed width for mobile
                      field.value === style.id 
                        ? 'border-2 border-primary ring-2 ring-primary ring-opacity-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                    onClick={() => field.onChange(style.id)}
                  >
                    <img
                      src={style.imagePath}
                      alt={style.text}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white py-1.5 px-2 text-xs">
                      {style.text}
                    </div>
                  </button>
                ))}
              </div>
            )}
          />
        </div>

        {/* Generate Button */}
        <Button
          variant={'customPrimary'}
          type="submit"
          disabled={!isFormValid || isPending}
          className="w-full gap-3 text-lg"
        >
          {isPending ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Sparkle className="w-5 h-5" />
              <span>Generate image</span>
            </>
          )}
        </Button>
      </form>

      {/* Key prop forces re-render when refreshReCaptcha changes */}
      {!refreshReCaptcha && (
        <GoogleReCaptcha 
          key={refreshReCaptcha ? 'refresh' : 'initial'} 
          action="KaiRecaptcha0911" 
          onVerify={onVerify} 
        />
      )}
    </div>
  );
}